import {
  call,
  put,
  race,
  select,
  take,
  delay,
  takeLatest,
} from "redux-saga/effects";
import { deleteSearchParams, getSearchParam, hasSearchParam } from "utils/url";
import * as selectors from "./selectors";
import {
  KEYWORD_TRIGGERED_BANNER_ERROR,
  KEYWORD_TRIGGERED_BANNER_SUCCESS,
  FORM_SUBMIT,
  WEBCODE_SKIP,
  WEBCODE_SUCCESS,
} from "actions/const";
import { CHANGE_TYPES } from "constants/search";
import { searchRequested, searchSuccess, searchError } from "actions";
import { searchProductService } from "services";
import SearchTracker, {
  DIMENSION,
  TYPE as TRACKING_TYPE,
} from "services/tracking/tracker";
import config from "config";
import { QUERY_PARAMS } from "constants/URL";

function* getWebcodeResult() {
  const webcodeRace = yield race({
    webcodeSuccess: take(WEBCODE_SUCCESS),
    webcodeSkip: take(WEBCODE_SKIP),
  });

  return webcodeRace;
}

const getSuggestTrackingValue = (changeType, action) => {
  if (
    changeType === CHANGE_TYPES.INITIALIZE &&
    hasSearchParam(QUERY_PARAMS.VALUE_BEFORE_AUTOSUGGEST)
  ) {
    return getSearchParam(QUERY_PARAMS.VALUE_BEFORE_AUTOSUGGEST);
  }

  if (action && action.valueBeforeSuggest) {
    return action.valueBeforeSuggest;
  }
  return "";
};

/**
 * generator function
 * starts the searchrequest to backend
 *
 * @param {String='replace','append'} updateType='replace' indicates the strategy to update the result
 * @param {oneof CHANGE_TYPES} changeType indicates if new Keyword Triggered Banner is loaded
 */
export function* fetchResultAsync(
  updateType = "replace",
  changeType = "",
  action,
) {
  // debounce filter selection
  yield delay(200);

  const filters = yield select(selectors.getFilters);
  const offset = yield select(selectors.getOffset);
  const limit = yield select(selectors.getItemsPerPage);
  const sortOptions = yield select(selectors.getSortOptions);
  const collapseLicenseForms = yield select(selectors.getCollapseLicenseForms);
  const rankingStrategy = yield select(selectors.getRankingStrategy);
  const trackingAction = SearchTracker.getActionFromChangeType(changeType);
  const suggestTrackingValue = getSuggestTrackingValue(changeType, action);

  deleteSearchParams([
    QUERY_PARAMS.IS_USER_SEARCH,
    QUERY_PARAMS.VALUE_BEFORE_AUTOSUGGEST,
  ]);

  const webcodeRace =
    updateType === "replace" &&
    (changeType === CHANGE_TYPES.QUERY ||
      changeType === CHANGE_TYPES.INITIALIZE)
      ? yield race({
          webcode: getWebcodeResult,
          webcodeTimeout: delay(config.webcodes.timeout),
        })
      : null;

  if (
    webcodeRace &&
    webcodeRace.webcode &&
    webcodeRace.webcode.webcodeSuccess
  ) {
    return;
  }

  yield put(searchRequested(filters, updateType));
  try {
    const result = yield call(searchProductService.fetchResult, {
      filters,
      offset,
      limit,
      sortOptions,
      collapseLicenseForms,
      rankingStrategy,
    });

    if (
      changeType === CHANGE_TYPES.QUERY ||
      changeType === CHANGE_TYPES.INITIALIZE
    ) {
      yield race({
        bannerSuccess: take(KEYWORD_TRIGGERED_BANNER_SUCCESS),
        bannerError: take(KEYWORD_TRIGGERED_BANNER_ERROR),
        bannerTimeout: delay(config.keywordTriggeredBanner.timeout),
      });
    }

    yield put(searchSuccess(result, updateType));
    SearchTracker.track(TRACKING_TYPE.SEARCH, [
      yield select(),
      trackingAction,
      suggestTrackingValue
        ? { [DIMENSION.USER_TERM]: [[suggestTrackingValue]] }
        : {},
    ]);
  } catch (e) {
    yield put(searchError(e));
  }
}

export function* submitSearchForm({ formNode }) {
  const webcodeRace = yield call(getWebcodeResult);

  if (webcodeRace && webcodeRace.webcodeSuccess) {
    return;
  }

  if (formNode.elements && formNode.elements.skipWebcode) {
    formNode.elements.skipWebcode.value = "true";
  }
  formNode.submit();
}

export function* watchFormSubmit() {
  yield takeLatest(FORM_SUBMIT, submitSearchForm);
}
